import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { buildSessionBridgeUrl } from "../../api/auth.ts";
import { getPagePath } from "../../pages/pageRegistry.ts";
import { useFavorites } from "../../hooks/useFavorites.ts";
import { useRecents } from "../../hooks/useRecents.ts";
import { useStartPageLabel } from "./userMenu.tsx";
import { useMediaQuery, breakpoints } from "../../hooks/useMediaQuery.ts";
import type { WebAdminPageMenuItem, WebAdminPage } from "../../types/session.ts";
import { StatusIcon } from "../ui/statusIcon.tsx";
import styles from "./sideNav.module.scss";

const chevronRightSvg = <StatusIcon kind="chevronRight" size={10} />;
const closeSvg = <StatusIcon kind="close" size={12} />;
const gripSvg = <StatusIcon kind="grip" size={14} />;
const starFilledSvg = <StatusIcon kind="starFilled" size={16} />;
const clockSvg = <StatusIcon kind="clock" size={18} />;

/** Ctrl/Cmd-click on a sidenav item opens the page in a new tab via the
 *  session bridge so the new tab inherits the current session without bouncing
 *  through SignIn. Plain click stays in-app via React Router. */
function useCtrlBridgeNav() {
  const { session } = useAuth();
  return useCallback((e: React.MouseEvent, path: string) => {
    if (!(e.ctrlKey || e.metaKey)) return;
    if (!session) return;
    e.preventDefault();
    const url = buildSessionBridgeUrl(path, session);
    window.open(url, "_blank", "noopener");
  }, [session]);
}

/** First letter of each word, max 2, uppercased. "Customer Service" → "CS". */
function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) {
    const w = words[0] ?? "";
    return w.slice(0, 2).toUpperCase();
  }
  return ((words[0]?.[0] ?? "") + (words[1]?.[0] ?? "")).toUpperCase();
}

// ---------------------------------------------------------------------------
// Tree types & build logic
// ---------------------------------------------------------------------------

interface MenuNode {
  id: number;
  page: WebAdminPage | null;
  children: MenuNode[];
  order: number;
}

function isPageAvailableForModule(page: WebAdminPage, activeModuleId: number): boolean {
  if (!page.Modules) return true; // no module restriction → available for all
  const modules = page.Modules.split(",").map(Number);
  return modules.includes(activeModuleId);
}

function buildMenuTree(
  pages: WebAdminPage[],
  menuItems: WebAdminPageMenuItem[],
  canRead: (id: number) => boolean,
  activeModuleId: number
): MenuNode[] {
  const pageMap = new Map(pages.map((p) => [p.RowID, p]));
  const nodeMap = new Map<number, MenuNode>();

  for (const item of menuItems) {
    nodeMap.set(item.ID, {
      id: item.ID,
      page: pageMap.get(item.RowID) ?? null,
      children: [],
      order: item.Order ?? 0,
    });
  }

  const roots: MenuNode[] = [];
  for (const item of menuItems) {
    const node = nodeMap.get(item.ID);
    if (!node) continue;
    if (!item.ParentID) {
      roots.push(node);
    } else {
      nodeMap.get(item.ParentID)?.children.push(node);
    }
  }

  function sort(nodes: MenuNode[]) {
    nodes.sort((a, b) => a.order - b.order);
    for (const n of nodes) sort(n.children);
  }
  sort(roots);

  function prune(nodes: MenuNode[]): MenuNode[] {
    return nodes.flatMap((node) => {
      const prunedChildren = prune(node.children);
      const p = node.page;

      // No page object — structural container, keep if it has visible children
      if (!p) {
        return prunedChildren.length > 0 ? [{ ...node, children: prunedChildren }] : [];
      }

      // Disabled, unavailable, or wrong module — remove this node and its children
      if (!p.Enabled || !p.Available || !isPageAvailableForModule(p, activeModuleId)) {
        return [];
      }

      // SUB (folder) nodes — keep as group header if they have visible children
      if (p.Type === "SUB") {
        return prunedChildren.length > 0 ? [{ ...node, children: prunedChildren }] : [];
      }

      // Leaf/page nodes — must pass permission check
      if (!canRead(p.RowID)) return [];

      return [{ ...node, children: prunedChildren }];
    });
  }

  return prune(roots);
}

/**
 * Rename the Home / start-page leaf (the node whose path resolves to "/") to
 * `label` (e.g. "Cockpit Dashboard" / "Classic Dashboard") so the menu entry
 * names the dashboard it opens instead of a generic "Dashboard" that collides
 * with the Dashboard grid. Clones only the touched nodes — never mutates cache.
 */
function relabelStartPage(nodes: MenuNode[], label: string): MenuNode[] {
  return nodes.map((node) => {
    const children = node.children.length ? relabelStartPage(node.children, label) : node.children;
    const p = node.page;
    if (p && p.Type !== "SUB" && getPagePath(p.Name) === "/") {
      return { ...node, page: { ...p, DisplayName: label }, children };
    }
    return children === node.children ? node : { ...node, children };
  });
}

function flatFallback(pages: WebAdminPage[], canRead: (id: number) => boolean, activeModuleId: number): MenuNode[] {
  return pages
    .filter((p) => p.Enabled && p.Available && p.Type !== "SUB" && canRead(p.RowID) && isPageAvailableForModule(p, activeModuleId))
    .map((p, i) => ({ id: p.RowID, page: p, children: [], order: i }));
}

// ---------------------------------------------------------------------------
// Tree filtering (search)
// ---------------------------------------------------------------------------

function filterTree(nodes: MenuNode[], query: string): MenuNode[] {
  const lowerQuery = query.toLowerCase();
  return nodes.flatMap((node) => {
    const filteredChildren = filterTree(node.children, query);
    const nameMatch = node.page?.DisplayName?.toLowerCase().includes(lowerQuery);
    if (nameMatch) {
      return [node]; // keep node with all its children
    }
    if (filteredChildren.length > 0) {
      return [{ ...node, children: filteredChildren }];
    }
    return [];
  });
}

function collectAllIds(nodes: MenuNode[]): Set<number> {
  const ids = new Set<number>();
  function walk(list: MenuNode[]) {
    for (const n of list) {
      ids.add(n.id);
      walk(n.children);
    }
  }
  walk(nodes);
  return ids;
}

// ---------------------------------------------------------------------------
// Active-page detection
// ---------------------------------------------------------------------------

function isPageActive(page: WebAdminPage, pathname: string, search: string): boolean {
  const to = getPagePath(page.Name);
  const qIdx = to.indexOf("?");
  const toPath = qIdx >= 0 ? to.slice(0, qIdx) : to;
  const toSearch = qIdx >= 0 ? to.slice(qIdx) : "";

  if (pathname !== toPath) return false;
  if (!toSearch) return true;

  const toParams = new URLSearchParams(toSearch);
  const curParams = new URLSearchParams(search);
  return (
    (toParams.get("name") ?? "").toLowerCase() ===
    (curParams.get("name") ?? "").toLowerCase()
  );
}

function hasActiveDescendant(node: MenuNode, pathname: string, search: string): boolean {
  if (node.page && node.page.Type !== "SUB" && isPageActive(node.page, pathname, search)) return true;
  return node.children.some((c) => hasActiveDescendant(c, pathname, search));
}

/**
 * Collect every SUB node id on the chain from a root down to the active page.
 * Used at mount / route change so the sidenav expands the full ancestor path of
 * the current page — not just the top-level group. Without this, a nested page
 * (e.g. Customer Service → Promotion → Promotion Search) shows only the
 * top-level "Customer Service" open and the user has to manually expand
 * "Promotion" to see the active item.
 */
function collectActiveAncestors(nodes: MenuNode[], pathname: string, search: string): Set<number> {
  const ids = new Set<number>();
  function walk(node: MenuNode): boolean {
    const selfActive = !!(node.page && node.page.Type !== "SUB" && isPageActive(node.page, pathname, search));
    let childActive = false;
    for (const c of node.children) {
      if (walk(c)) childActive = true;
    }
    if (childActive && node.children.length > 0) ids.add(node.id);
    return selfActive || childActive;
  }
  for (const n of nodes) walk(n);
  return ids;
}

// ---------------------------------------------------------------------------
// NavItem
// ---------------------------------------------------------------------------

interface NavItemProps {
  node: MenuNode;
  depth: number;
  isFirst?: boolean;
  openGroups: Set<number>;
  onToggle: (id: number) => void;
  pathname: string;
  search: string;
}

function NavItem({ node, depth, isFirst, openGroups, onToggle, pathname, search }: NavItemProps) {
  const ctrlBridge = useCtrlBridgeNav();
  const { page, children } = node;
  if (!page) return null;

  // Decreasing indent: level 1 = 14px, then shrinks (14/12/10/8/8…) so deep
  // trees don't run off the rail.
  const perLevel = [14, 12, 10, 8];
  let indent = 12;
  for (let i = 0; i < depth; i++) indent += perLevel[Math.min(i, perLevel.length - 1)]!;

  if (page.Type === "SUB") {
    const isOpen = openGroups.has(node.id);
    const groupLevelClass =
      depth === 0 ? styles.groupBtnL0 :
      depth === 1 ? styles.groupBtnL1 : styles.groupBtnLN;
    const groupSepClass = depth === 0 && !isFirst ? ` ${styles.groupBtnL0Sep}` : "";
    const arrowLevelClass =
      depth === 0 ? ` ${styles.groupArrowL0}` :
      depth === 1 ? ` ${styles.groupArrowL1}` : "";
    return (
      <div>
        <button
          onClick={() => onToggle(node.id)}
          className={`${styles.groupBtn} ${groupLevelClass}${groupSepClass}`}
          style={{ paddingLeft: `${indent}px` }}
          aria-expanded={isOpen}
        >
          <span className={`${styles.groupArrow}${isOpen ? ` ${styles.groupArrowOpen}` : ""}${arrowLevelClass}`}>
            {chevronRightSvg}
          </span>
          {page.DisplayName}
        </button>
        {isOpen && children.length > 0 && (
          <div role="group" aria-label={page.DisplayName}>
            {children.map((child) => (
              <NavItem
                key={child.id}
                node={child}
                depth={depth + 1}
                openGroups={openGroups}
                onToggle={onToggle}
                pathname={pathname}
                search={search}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  const active = isPageActive(page, pathname, search);
  const path = getPagePath(page.Name);
  return (
    <Link
      to={path}
      className={`${styles.navLink}${depth > 0 ? ` ${styles.navLinkNested}` : ""}${active ? ` ${styles.navLinkActive}` : ""}`}
      style={{ padding: `5px 12px 5px ${indent}px` }}
      aria-current={active ? "page" : undefined}
      onClick={(e) => ctrlBridge(e, path)}
    >
      {page.DisplayName}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// SideNav
// ---------------------------------------------------------------------------

export function SideNav() {
  const { cache, canRead, activeEndpoint } = useAuth();
  const ctrlBridge = useCtrlBridgeNav();
  const { pathname, search } = useLocation();
  const [searchText, setSearchText] = useState("");
  const { favorites, removeFavorite, reorderFavorites } = useFavorites();
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<{ index: number; position: "before" | "after" } | null>(null);

  const activeModuleId = activeEndpoint?.ModuleID ?? 0;

  const pages = useMemo(() => cache?.PageInfo?.Page ?? [], [cache]);
  const menuItems = useMemo(() => cache?.PageInfo?.MenuItem ?? [], [cache]);

  const startPageLabel = useStartPageLabel();
  const tree = useMemo(
    () => {
      const built = menuItems.length > 0
        ? buildMenuTree(pages, menuItems, canRead, activeModuleId)
        : flatFallback(pages, canRead, activeModuleId);
      return startPageLabel ? relabelStartPage(built, startPageLabel) : built;
    },
    [pages, menuItems, canRead, activeModuleId, startPageLabel]
  );

  const filteredTree = useMemo(
    () => (searchText.trim() ? filterTree(tree, searchText.trim()) : tree),
    [tree, searchText]
  );

  // Default: every SUB on the path to the active page is open, so nested
  // breadcrumb chains (e.g. Customer Service → Promotion → Promotion Search)
  // reveal the current item without a manual click. Falls back to the first
  // top-level group when nothing is active (e.g. on dashboard).
  const defaultOpen = useMemo(() => {
    const path = collectActiveAncestors(tree, pathname, search);
    if (path.size > 0) return path;
    const first = tree.find((n) => n.children.length > 0);
    return first ? new Set([first.id]) : new Set<number>();
  }, [tree, pathname, search]);
  const [openGroups, setOpenGroups] = useState<Set<number>>(defaultOpen);

  useEffect(() => {
    setOpenGroups(new Set(defaultOpen));
  }, [defaultOpen]);

  // When searching, expand all groups in filtered tree
  const effectiveOpenGroups = useMemo(() => {
    if (searchText.trim()) {
      return collectAllIds(filteredTree);
    }
    return openGroups;
  }, [searchText, filteredTree, openGroups]);

  // Top-level SUB ids — used to enforce "only one top-level group open at a time"
  const topLevelIds = useMemo(() => new Set(tree.map((n) => n.id)), [tree]);

  const toggleGroup = (id: number) =>
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        // Opening a top-level group → close other top-level siblings first so
        // the visible list length stays roughly constant regardless of tree depth
        if (topLevelIds.has(id)) {
          for (const tid of topLevelIds) next.delete(tid);
        }
        next.add(id);
      }
      return next;
    });

  // Favorites: resolve page names to display names (keep unresolved ones too)
  const favoriteEntries = useMemo(() => {
    if (favorites.length === 0) return [];
    return favorites.map((name) => {
      const page = pages.find((p) => p.Name.toLowerCase() === name);
      return { name, page: page ?? null, displayName: page?.DisplayName ?? name };
    });
  }, [favorites, pages]);

  const [favsOpen, setFavsOpen] = useState(true);

  const { recents } = useRecents();
  const recentEntries = useMemo(() => {
    if (recents.length === 0) return [];
    return recents
      .map((name) => {
        const page = pages.find((p) => p.Name.toLowerCase() === name);
        return page ? { name, page, displayName: page.DisplayName } : null;
      })
      .filter((x): x is { name: string; page: WebAdminPage; displayName: string } => x !== null);
  }, [recents, pages]);
  const [recentsOpen, setRecentsOpen] = useState(true);

  // Rail mode activates between md and lg (~768–1023 px viewport). Below md
  // the sidebar is an overlay drawer (app.tsx); above lg the full sidebar.
  const isMdPlus = useMediaQuery(breakpoints.md);
  const isLgPlus = useMediaQuery(breakpoints.lg);
  const isRail = isMdPlus && !isLgPlus;

  if (isRail) {
    return (
      <RailSideNav
        tree={tree}
        favoriteEntries={favoriteEntries}
        recentEntries={recentEntries}
        pathname={pathname}
        search={search}
      />
    );
  }

  return (
    <nav className={styles.nav} aria-label="Main navigation">
      <div className={styles.searchBox}>
        <div className={styles.searchInputWrap}>
          <input
            id="sidenav-search"
            type="search"
            name="sidenav-search-no-history"
            placeholder="Search pages..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Escape" && searchText) { e.preventDefault(); setSearchText(""); } }}
            className={styles.searchInput}
            aria-label="Search navigation pages"
            autoComplete="off"
            spellCheck={false}
          />
          {searchText ? (
            <button
              type="button"
              className={styles.searchClear}
              onClick={() => {
                setSearchText("");
                document.getElementById("sidenav-search")?.focus();
              }}
              aria-label="Clear search"
              title="Clear search"
            >
              ×
            </button>
          ) : (
            <kbd className={styles.searchKbd} aria-hidden="true">Ctrl+Shift+F</kbd>
          )}
        </div>
      </div>
      {favoriteEntries.length > 0 && !searchText.trim() && (
        <div className={styles.favoritesSection}>
          <button
            onClick={() => setFavsOpen((v) => !v)}
            className={styles.groupBtn}
            style={{ padding: "7px 12px 7px 12px" }}
            aria-expanded={favsOpen}
          >
            <span className={`${styles.groupArrow}${favsOpen ? ` ${styles.groupArrowOpen}` : ""}`}>
              {chevronRightSvg}
            </span>
            <StatusIcon kind="starFilled" size={14} style={{ color: "var(--color-warning)", flexShrink: 0 }} />
            Favorites
          </button>
          {favsOpen && (
            <div role="group" aria-label="Favorites">
              {favoriteEntries.map((entry, i) => {
                const active = entry.page ? isPageActive(entry.page, pathname, search) : false;
                const isDragging = dragIdx === i;
                const dropClass = dropIdx && dropIdx.index === i
                  ? (dropIdx.position === "before" ? styles.favItemDropBefore : styles.favItemDropAfter)
                  : "";
                return (
                  <div
                    key={entry.name}
                    className={`${styles.favItem}${isDragging ? ` ${styles.favItemDragging}` : ""}${dropClass ? ` ${dropClass}` : ""}`}
                    onDragOver={(e) => {
                      if (dragIdx === null) return;
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                      const rect = e.currentTarget.getBoundingClientRect();
                      const position = e.clientY < rect.top + rect.height / 2 ? "before" : "after";
                      setDropIdx((prev) => (prev?.index === i && prev.position === position ? prev : { index: i, position }));
                    }}
                    onDragLeave={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
                        setDropIdx((prev) => (prev?.index === i ? null : prev));
                      }
                    }}
                    onDrop={(e) => {
                      if (dragIdx === null) return;
                      e.preventDefault();
                      const target = dropIdx?.index === i ? dropIdx : { index: i, position: "after" as const };
                      const insertBefore = target.position === "before" ? target.index : target.index + 1;
                      reorderFavorites(dragIdx, insertBefore);
                      setDragIdx(null);
                      setDropIdx(null);
                    }}
                  >
                    <span
                      className={styles.favDragHandle}
                      draggable
                      role="button"
                      aria-label={`Reorder ${entry.displayName}`}
                      title="Drag to reorder"
                      onDragStart={(e) => {
                        setDragIdx(i);
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", entry.name);
                      }}
                      onDragEnd={() => {
                        setDragIdx(null);
                        setDropIdx(null);
                      }}
                    >
                      {gripSvg}
                    </span>
                    <Link
                      to={getPagePath(entry.name)}
                      className={`${styles.navLink}${active ? ` ${styles.navLinkActive}` : ""}`}
                      style={{ padding: "5px 12px 5px 10px", flex: 1 }}
                      aria-current={active ? "page" : undefined}
                      onClick={(e) => ctrlBridge(e, getPagePath(entry.name))}
                    >
                      {entry.displayName}
                    </Link>
                    <button
                      onClick={() => removeFavorite(entry.name)}
                      className={styles.favRemoveBtn}
                      title={`Remove ${entry.displayName} from favorites`}
                      aria-label={`Remove ${entry.displayName} from favorites`}
                    >
                      {closeSvg}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          <div className={styles.favDivider} />
        </div>
      )}
      {recentEntries.length > 0 && !searchText.trim() && (
        <div className={styles.favoritesSection}>
          <button
            onClick={() => setRecentsOpen((v) => !v)}
            className={styles.groupBtn}
            style={{ padding: "7px 12px 7px 12px" }}
            aria-expanded={recentsOpen}
          >
            <span className={`${styles.groupArrow}${recentsOpen ? ` ${styles.groupArrowOpen}` : ""}`}>
              {chevronRightSvg}
            </span>
            Recent
          </button>
          {recentsOpen && (
            <div role="group" aria-label="Recent pages">
              {recentEntries.map((entry) => {
                const active = isPageActive(entry.page, pathname, search);
                return (
                  <Link
                    key={entry.name}
                    to={getPagePath(entry.name)}
                    className={`${styles.navLink}${active ? ` ${styles.navLinkActive}` : ""}`}
                    style={{ padding: "5px 12px 5px 26px" }}
                    aria-current={active ? "page" : undefined}
                    onClick={(e) => ctrlBridge(e, getPagePath(entry.name))}
                  >
                    {entry.displayName}
                  </Link>
                );
              })}
            </div>
          )}
          <div className={styles.favDivider} />
        </div>
      )}
      <div className={styles.treeWrap}>
        {filteredTree.map((node, i) => (
          <NavItem
            key={node.id}
            node={node}
            depth={0}
            isFirst={i === 0}
            openGroups={effectiveOpenGroups}
            onToggle={toggleGroup}
            pathname={pathname}
            search={search}
          />
        ))}
        {searchText.trim() && filteredTree.length === 0 && (
          <div className={styles.noMatches} role="status">No matches</div>
        )}
      </div>
    </nav>
  );
}

// ---------------------------------------------------------------------------
// Rail mode (narrow viewport)
// ---------------------------------------------------------------------------

interface FavEntry { name: string; page: WebAdminPage | null; displayName: string; }
interface RecentEntry { name: string; page: WebAdminPage; displayName: string; }

interface RailSideNavProps {
  tree: MenuNode[];
  favoriteEntries: FavEntry[];
  recentEntries: RecentEntry[];
  pathname: string;
  search: string;
}

type FlyoutKey = "fav" | "recent" | `sub-${number}` | null;

function RailSideNav({ tree, favoriteEntries, recentEntries, pathname, search }: RailSideNavProps) {
  const ctrlBridge = useCtrlBridgeNav();
  const [openFlyout, setOpenFlyout] = useState<FlyoutKey>(null);
  const navRef = useRef<HTMLElement>(null);

  // Close flyout on outside click / route change
  useEffect(() => {
    if (openFlyout === null) return;
    function onDown(e: MouseEvent) {
      if (navRef.current && !navRef.current.contains(e.target as Node)) setOpenFlyout(null);
    }
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [openFlyout]);

  useEffect(() => { setOpenFlyout(null); }, [pathname, search]);

  return (
    <nav ref={navRef} className={styles.navRail} aria-label="Main navigation (narrow)">
      {favoriteEntries.length > 0 && (
        <RailFlyoutItem
          open={openFlyout === "fav"}
          onToggle={() => setOpenFlyout((k) => (k === "fav" ? null : "fav"))}
          title="Favorites"
          trigger={<span style={{ color: "var(--color-warning)" }}>{starFilledSvg}</span>}
        >
          <div className={styles.railFlyoutHeader}>Favorites</div>
          {favoriteEntries.map((entry) => {
            const active = entry.page ? isPageActive(entry.page, pathname, search) : false;
            return (
              <Link
                key={entry.name}
                to={getPagePath(entry.name)}
                className={`${styles.railFlyoutLink}${active ? ` ${styles.railFlyoutLinkActive}` : ""}`}
                onClick={(e) => ctrlBridge(e, getPagePath(entry.name))}
              >
                {entry.displayName}
              </Link>
            );
          })}
        </RailFlyoutItem>
      )}

      {recentEntries.length > 0 && (
        <RailFlyoutItem
          open={openFlyout === "recent"}
          onToggle={() => setOpenFlyout((k) => (k === "recent" ? null : "recent"))}
          title="Recent"
          trigger={clockSvg}
        >
          <div className={styles.railFlyoutHeader}>Recent</div>
          {recentEntries.map((entry) => {
            const active = isPageActive(entry.page, pathname, search);
            return (
              <Link
                key={entry.name}
                to={getPagePath(entry.name)}
                className={`${styles.railFlyoutLink}${active ? ` ${styles.railFlyoutLinkActive}` : ""}`}
                onClick={(e) => ctrlBridge(e, getPagePath(entry.name))}
              >
                {entry.displayName}
              </Link>
            );
          })}
        </RailFlyoutItem>
      )}

      {tree.map((node) => {
        const page = node.page;
        if (!page) return null;
        const title = page.DisplayName;
        if (page.Type !== "SUB") {
          // Top-level leaf — clickable directly
          const active = isPageActive(page, pathname, search);
          return (
            <Link
              key={node.id}
              to={getPagePath(page.Name)}
              className={`${styles.railBtn}${active ? ` ${styles.railBtnActive}` : ""}`}
              title={title}
              aria-label={title}
              onClick={(e) => ctrlBridge(e, getPagePath(page.Name))}
            >
              <span className={styles.railMono}>{monogram(title)}</span>
            </Link>
          );
        }
        const flyoutId: FlyoutKey = `sub-${node.id}`;
        const active = hasActiveDescendant(node, pathname, search);
        return (
          <RailFlyoutItem
            key={node.id}
            open={openFlyout === flyoutId}
            onToggle={() => setOpenFlyout((k) => (k === flyoutId ? null : flyoutId))}
            title={title}
            trigger={<span className={styles.railMono}>{monogram(title)}</span>}
            triggerActive={active}
          >
            <div className={styles.railFlyoutHeader}>{title}</div>
            <RailFlyoutTree nodes={node.children} pathname={pathname} search={search} />
          </RailFlyoutItem>
        );
      })}
    </nav>
  );
}

interface RailFlyoutItemProps {
  open: boolean;
  onToggle: () => void;
  title: string;
  trigger: React.ReactNode;
  triggerActive?: boolean;
  children: React.ReactNode;
}

function RailFlyoutItem({ open, onToggle, title, trigger, triggerActive, children }: RailFlyoutItemProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    if (!open || !btnRef.current) { setCoords(null); return; }
    const r = btnRef.current.getBoundingClientRect();
    setCoords({ top: r.top, left: r.right + 4 });
  }, [open]);

  return (
    <div className={styles.railItem}>
      <button
        ref={btnRef}
        type="button"
        className={`${styles.railBtn}${triggerActive ? ` ${styles.railBtnActive}` : ""}${open ? ` ${styles.railBtnOpen}` : ""}`}
        onClick={onToggle}
        title={title}
        aria-label={title}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        {trigger}
      </button>
      {open && coords && (
        <div
          className={styles.railFlyout}
          role="menu"
          style={{ top: coords.top, left: coords.left }}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function RailFlyoutTree({ nodes, pathname, search }: { nodes: MenuNode[]; pathname: string; search: string }) {
  const ctrlBridge = useCtrlBridgeNav();
  return (
    <>
      {nodes.map((node) => {
        const page = node.page;
        if (!page) return null;
        if (page.Type === "SUB") {
          return (
            <div key={node.id} className={styles.railFlyoutGroup}>
              <div className={styles.railFlyoutGroupLabel}>{page.DisplayName}</div>
              <RailFlyoutTree nodes={node.children} pathname={pathname} search={search} />
            </div>
          );
        }
        const active = isPageActive(page, pathname, search);
        const path = getPagePath(page.Name);
        return (
          <Link
            key={node.id}
            to={path}
            className={`${styles.railFlyoutLink}${active ? ` ${styles.railFlyoutLinkActive}` : ""}`}
            onClick={(e) => ctrlBridge(e, path)}
          >
            {page.DisplayName}
          </Link>
        );
      })}
    </>
  );
}
